import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, ChevronDown, List, Map as MapIcon, Wifi, Car, Package, FlameKindling, Droplets, X, SlidersHorizontal, TrendingUp, Building2, Eye, Lock } from 'lucide-react';
import { useRentalProperties } from '@/hooks/useRental';
import { Spinner, EmptyState, Pagination } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils';
import type { RentalPropertyFilter, RentalProperty } from '@/types';
import PropertyMap from '../../components/rental/PropertyMap';
import { RentalFilter } from '../../components/rental/RentalFilter';
import RentalCard from '../../components/card/RentalCard';
import { Select, MenuItem } from '@mui/material';
import { useAuthStore } from '@/store/authStore';

/* ─── helpers ─── */
function fmtRent(str: string) {
  const n = parseFloat(str);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}tr/th`;
  return formatNumber(n) + '/th';
}
function getPrimaryImg(images: any[]) {
  if (!images?.length) return null;
  return (images.find((i) => i.isPrimary) ?? images[0])?.image?.publicUrl ?? null;
}
function typeEmoji(t: string) {
  const m: Record<string, string> = {
    restaurant: '🍽️',
    cafe: '☕',
    bar: '🍸',
    bakery: '🥐',
    'food court stall': '🏪',
    'food court': '🏪',
  };
  return m[t?.toLowerCase()] ?? '🏢';
}

const LIMIT = 9;


/* ════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════ */
export default function PropertyListPage() {
  const { isAuthenticated } = useAuthStore();
  const [filters, setFilters] = useState<RentalPropertyFilter>({
    page: 1,
    limit: LIMIT,
    language: 'vi',
    orderBy: 'createdAt',
    orderDirection: 'DESC',
  });
  const [viewMode, setViewMode] = useState<'split' | 'list' | 'map'>('split');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useRentalProperties(filters);
  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const handleFilterSearch = (newFilters: any) => {
    setFilters((f) => ({
      limit: f.limit,
      orderBy: f.orderBy,
      orderDirection: f.orderDirection,
      language: f.language,
      ...newFilters,
      page: 1,
    }));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-6">
        <div className="section-label">Cho thuê</div>
        <div className="flex items-end justify-between flex-wrap gap-2">
          <h1 className="section-title mb-0">Danh sách cho thuê</h1>
          <div className="flex items-center gap-3">
            {total > 0 && (
              <span className="text-sm" style={{ color: 'var(--text-3)' }}>
                {total} kết quả
              </span>
            )}
            {!isAuthenticated && (
              <Link
                to="/login"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors hover:border-orange-400 hover:text-orange-500"
                style={{ borderColor: 'var(--border-c)', color: 'var(--text-3)' }}
              >
                <Lock size={11} /> Đăng nhập để lọc nâng cao
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="sticky top-[72px] z-30 mb-4">
        <RentalFilter type="property" onSearch={handleFilterSearch} initialFilters={filters} />
      </div>

      <div className="flex flex-wrap items-center justify-between mb-4 gap-4">
        {/* View mode */}
        <div className="flex items-center gap-0 border rounded-lg overflow-hidden bg-white" style={{ borderColor: 'var(--border-c)' }}>
          {(
            [
              ['split', 'Cả hai'],
              ['list', 'Danh sách'],
              ['map', 'Bản đồ'],
            ] as const
          ).map(([mode, lbl]) => (
            <button
              key={mode}
              onClick={() => {
                setViewMode(mode);
                setTimeout(() => {
                  window.dispatchEvent(new Event('resize'));
                }, 100);
              }}
              className={cn('flex items-center gap-1.5 px-4 h-9 text-xs font-medium transition-colors', viewMode === mode ? 'text-white' : 'hover:bg-gray-50')}
              style={viewMode === mode ? { background: 'var(--brand)' } : { color: 'var(--text-2)' }}
            >
              {mode === 'list' ? (
                <List size={13} />
              ) : mode === 'map' ? (
                <MapIcon size={13} />
              ) : (
                <>
                  <List size={11} />
                  <MapIcon size={11} />
                </>
              )}
              <span className="hidden sm:inline">{lbl}</span>
            </button>
          ))}
        </div>

        {/* Sort options aligned right */}
        <Select
          value={`${filters.orderBy}|${filters.orderDirection}`}
          onChange={(e) => {
            const [orderBy, orderDirection] = (e.target.value as string).split('|') as [any, 'ASC' | 'DESC'];
            setFilters(prev => ({ ...prev, orderBy, orderDirection, page: 1 }));
          }}
          size="small"
          sx={{
            minWidth: '160px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            bgcolor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e5e7eb',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f97316',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f97316',
            }
          }}
        >
          <MenuItem value="createdAt|DESC" sx={{ fontSize: '14px' }}>Mới nhất</MenuItem>
          <MenuItem value="budgetMin|ASC" sx={{ fontSize: '14px' }}>Giá thuê tăng dần</MenuItem>
          <MenuItem value="budgetMin|DESC" sx={{ fontSize: '14px' }}>Giá thuê giảm dần</MenuItem>
          <MenuItem value="area|DESC" sx={{ fontSize: '14px' }}>Diện tích lớn nhất</MenuItem>
        </Select>
      </div>

      {/* ── Content ── */}
      {isLoading ? (
        <Spinner />
      ) : (
        <div
          className={cn(
            'flex gap-4 flex-1 min-h-0',
            viewMode === 'split' && 'h-[calc(100vh-230px)] min-h-[580px] overflow-hidden',
            viewMode === 'map' && 'flex-col'
          )}
        >
          {/* List panel */}
          {viewMode !== 'map' && (
            <div
              className={cn(
                'flex flex-col gap-3',
                viewMode === 'split'
                  ? 'w-full lg:w-[480px] xl:w-[540px] lg:shrink-0 h-full overflow-y-auto pr-2 custom-scrollbar justify-between'
                  : 'flex-1'
              )}
            >
              {!items.length ? (
                <EmptyState title="Không tìm thấy mặt bằng" description="Thử thay đổi bộ lọc" icon="🏢" />
              ) : (
                <>
                  <div
                    className={cn(
                      'grid gap-3 transition-opacity',
                      viewMode === 'list'
                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                        : 'grid-cols-1',
                      isFetching && 'opacity-60'
                    )}
                  >
                    {items.map((item) => (
                      <RentalCard
                        key={item.id}
                        item={item}
                        layout={viewMode === 'split' ? 'horizontal' : 'vertical'}
                        isSelected={selectedId === item.id}
                        onMouseEnter={() => setSelectedId(item.id)}
                      />
                    ))}
                  </div>
                  <div className="pt-2 mt-auto">
                    <Pagination
                      page={filters.page ?? 1}
                      totalPages={totalPages}
                      onChange={(p) => setFilters((f) => ({ ...f, page: p }))}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Map panel */}
          {viewMode !== 'list' && (
            <div
              className={cn(
                'rounded-2xl overflow-hidden border flex flex-col z-10 relative flex-1 min-h-[450px]',
                viewMode === 'split' ? 'h-full flex-1' : 'w-full min-h-[600px] flex-1',
                viewMode === 'map' && 'min-h-[600px] h-[calc(100vh-220px)]'
              )}
              style={{
                borderColor: 'var(--border-c)',
              }}
            >
              <div className="flex-1 w-full relative min-h-[450px] h-full">
                <PropertyMap
                  items={items}
                  selected={selectedId}
                  onSelect={setSelectedId}
                  locationQuery={filters.district && filters.province ? `${filters.district}, ${filters.province}` : filters.province}
                />
              </div>

              {/* Selected card overlay */}
              {selectedId &&
                (() => {
                  const item = items.find((i) => i.id === selectedId);
                  if (!item) return null;
                  return (
                    <div
                      className="absolute bottom-4 left-4 rounded-xl border p-3 flex gap-3 shadow-lg animate-fade-up w-[calc(100%-2rem)] max-w-[420px] bg-white z-[1000]"
                      style={{
                        background: 'var(--surface-0)',
                        borderColor: 'var(--border-c)',
                      }}
                    >
                      <div className="w-16 h-14 rounded-lg overflow-hidden shrink-0">
                        {getPrimaryImg(item.images) ? (
                          <img src={getPrimaryImg(item.images)!} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl" style={{ background: 'var(--brand-pale)' }}>
                            {typeEmoji(item.businessType)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate" style={{ color: 'var(--text-1)' }}>
                          {item.title}
                        </p>
                        <p className="font-bold text-base" style={{ color: 'var(--brand)' }}>
                          {fmtRent(item.monthlyRent)} VND
                        </p>
                        <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>
                          {item.area} {item.areaUnit} · {item.district?.name}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Link
                          to={`/cho-thue/${item.id}`}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg text-white"
                          style={{ background: 'var(--brand)' }}
                        >
                          <Eye size={11} /> Xem
                        </Link>
                        <button
                          onClick={() => setSelectedId(null)}
                          className="flex items-center justify-center w-full py-1 text-xs rounded-lg border hover:bg-gray-50 transition-colors"
                          style={{
                            borderColor: 'var(--border-c)',
                            color: 'var(--text-3)',
                          }}
                        >
                          <X size={11} />
                        </button>
                      </div>
                    </div>
                  );
                })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

